import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { kCustomConstructionWorkItemCategoryId } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/usePredefinedConstructionItems';
import {
  useConstructionJobScheduleContext,
  useScheduleConstructionJobs,
} from '@euler/app/flows/task/pages/construction/ConstructionJobScheduleContext';
import { ConstructionWorkItemInfo } from '@euler/app/flows/task/pages/construction/functions/useConstructionWorkItems';
import { useHandleConstructionWorkItemPress } from '@euler/app/flows/task/pages/construction/functions/useHandleConstructionWorkItemPress';
import { Center } from '@euler/components/Center';
import { Input } from '@euler/components/form/Input';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { FormController, useFormController } from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { showAlert, sleep } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import {
  forwardRef,
  memo,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

type Data = {
  title: string;
  categoryId: string;
};

const Section = memo(
  ({
    title,
    style,
    children,
  }: {
    title: string;
    children?: ReactNode;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          margin: ${rpx(15)}px;
        `}
        style={style}
      >
        <Typography.Label medium color="#333" size={rpx(15)}>
          {title}
        </Typography.Label>
        <View
          css={`
            margin-top: ${rpx(5)}px;
          `}
        >
          {children}
        </View>
      </View>
    );
  },
);

type Ref = {
  controller: FormController<Data>;
};

const Content = memo(
  forwardRef<Ref, any>((_, ref) => {
    const { rpx } = useResponsiveContext();
    const { categories$ } = useConstructionJobScheduleContext();
    const [categories] = useBehaviorSubject(categories$);
    const controller = useFormController<Data>({
      title: {
        required: true,
        defaultValue: '',
      },
      categoryId: {
        required: true,
        defaultValue: categories[0].id,
      },
    });

    useImperativeHandle(ref, () => ({
      controller,
    }));

    const { form } = controller;

    return (
      <>
        <Section title="施工名称">
          <Input
            placeholder="输入施工项目名称"
            keyboardType="default"
            value={form.title.value}
            errorMsg={form.title.error}
            clearButtonMode="always"
            returnKeyType="done"
            onBlur={form.title.onBlur}
            onChangeText={form.title.update}
            errorBottomOffset={rpx(4)}
            css={`
              padding-top: ${rpx(8)}px;
              padding-bottom: ${rpx(8)}px;
              height: ${rpx(48)}px;
            `}
          />
        </Section>
        <Section title="施工类型">
          {categories
            .filter(x => x.id !== kCustomConstructionWorkItemCategoryId)
            .map((category, i) => (
              <TouchableOpacity
                key={category.id}
                css={`
                  flex-direction: row;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: ${i === 0 ? rpx(10) : 0}px;
                  border-top-width: 1px;
                  border-top-color: ${i === 0 ? '#eee' : 'transparent'};
                  border-bottom-width: 1px;
                  border-bottom-color: ${i === categories.length - 1
                    ? '#eee'
                    : '#eee'};
                `}
                onPress={() => form.categoryId.update(category.id)}
              >
                <View
                  css={`
                    flex: 1;
                    margin-right: ${rpx(10)}px;
                    padding: ${rpx(5)}px 0;
                  `}
                >
                  <Typography.Label regular size={rpx(16)} color="#555">
                    {category.name}
                  </Typography.Label>
                  <Typography.Label light size={rpx(12)} color={'#888'}>
                    {category.template.procedures.map(x => x.name).join('、')}
                  </Typography.Label>
                </View>
                <Icon
                  name="check"
                  size={rpx(24)}
                  color={
                    category.id === form.categoryId.value
                      ? '#000'
                      : 'transparent'
                  }
                />
              </TouchableOpacity>
            ))}
        </Section>
      </>
    );
  }),
);

export const CustomWorkItemBottomSheetModal = memo(
  ({ visible, onDismiss }: { visible?: boolean; onDismiss?: () => void }) => {
    useToggleAutomaticKeyboard(true);

    const { rpx } = useResponsiveContext();
    const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();
    const { categories$ } = useConstructionJobScheduleContext();
    const [categories] = useBehaviorSubject(categories$);
    const { selectedWorkItems } = useScheduleConstructionJobs();

    const ref = useRef<Ref>(null);

    const onItemPress = useHandleConstructionWorkItemPress();

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: visible,
        topMargin: navBarHeight,
        minSnapPoint: '50%',
        mediumSnapPoint: '75%',
        index: 1,
        enableDismissOnClose: true,
        enablePanDownToClose: true,
        dismissWhenPressBackdrop: true,
        onDismiss,
      });
    const animatedPosition = useSharedValue(0);
    const height = useGeneralBottomSheetHeight();
    const style = useAnimatedStyle(() => {
      return {
        opacity: interpolate(
          animatedPosition.value,
          [(height * 2) / 4, height],
          [1, 0],
        ),
      };
    });

    const onCancel = usePersistFn(() => {
      bottomSheetModalRef.current?.dismiss();
    });

    const onDone = usePersistFn(async () => {
      const controller = ref.current?.controller;
      if (!controller) return;

      const valid = await controller.validateAll();
      if (!valid) return;

      const name = controller.form.title.value.trim();
      const categoryId = controller.form.categoryId.value;
      const category = categories.find(x => x.id === categoryId);
      const workItems = categories.flatMap(x => x.groups.flatMap(g => g.items));

      if (selectedWorkItems.some(x => x.workItem.name === name)) {
        void showAlert('提示', `"${name}"已经在施工项目列表中!`);
        return;
      }

      bottomSheetModalRef.current?.dismiss({
        duration: 200,
        easing: Easing.inOut(Easing.linear),
      });

      await sleep(250);

      const existing = workItems.find(x => x.name === name);
      if (existing) {
        onItemPress(existing);
        return;
      }

      const item: ConstructionWorkItemInfo = {
        name,
        categoryId: kCustomConstructionWorkItemCategoryId,
        pyInitials: getPyInitials(name),
        details: [],
        procedures: category?.template.procedures ?? [],
      };

      onItemPress(item);
    });

    const Footer = usePersistFn((props: BottomSheetFooterProps) => {
      return (
        <BottomSheetFooter {...props}>
          <BottomAction containerStyle={style}>
            <BottomAction.Button
              text="返回"
              variant="default"
              onPress={onCancel}
            />
            <BottomAction.Button
              text="添加"
              variant="primary"
              css={`
                margin-left: ${rpx(15)}px;
              `}
              onPress={onDone}
            />
          </BottomAction>
        </BottomSheetFooter>
      );
    });

    const Header = useCallback(
      () => (
        <Center
          css={`
            padding: ${rpx(10)}px 0;
            border-bottom-width: 1px;
            border-bottom-color: #f1f1f1;
            height: ${rpx(44)}px;
          `}
        >
          <Typography.Label size={rpx(16)} color="#333">
            添加自定义施工项目
          </Typography.Label>
        </Center>
      ),
      [rpx],
    );

    const ScrollViewComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    return (
      <>
        <BottomSheetModal
          {...bottomSheetProps}
          footerComponent={Footer}
          animatedPosition={animatedPosition}
          keyboardBehavior="interactive"
          keyboardBlurBehavior="none"
          ref={bottomSheetModalRef}
        >
          <ScrollViewComponent
            contentContainerStyle={{
              paddingBottom: insets.bottom + rpx(15),
            }}
          >
            <Header />
            <Content ref={ref} />
          </ScrollViewComponent>
        </BottomSheetModal>
      </>
    );
  },
);
